import React, { useEffect, useState } from "react";
import { useNoIndex } from "@/lib/usePageSeo";
import { trackEvent } from "@/lib/analytics";
import { useCart, toLineItemConfig } from "@/contexts/CartContext";
import { api } from "@/lib/api";
import { money, listMoney, usdApprox, useCurrency, getCurrency } from "@/lib/currency";
import { ChargeDisclosure } from "@/components/util/ChargeDisclosure";
import { useSalesTax } from "@/lib/tax";
import { Lock, Shield } from "lucide-react";
import { toast } from "sonner";

// Per-format AWG price paid for each Additional Main Artwork Format.
// Kept in sync with MAIN_ARTWORK_FORMAT_PRICE in PackageBuilder.jsx.
const MAIN_ARTWORK_FORMAT_PRICE = 75;
const MAIN_ARTWORK_FORMAT_PRICE_USD = 42;

// Normalise a selected_formats entry. Older cart writes (pre-2026-02-24)
// stored the entry as a plain string ("Social Square — 1080 × 1080 px");
// newer writes store { id, label, dimensions?, width?, height?, unit? }.
// Return a display-ready { label, sub } tuple either way.
const normaliseFormatEntry = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") {
    const parts = entry.split(" — ");
    return { label: parts[0] || entry, sub: parts.slice(1).join(" — ") };
  }
  const dims = entry.dimensions
    || (entry.width && entry.height && entry.unit
      ? `${entry.width} × ${entry.height} ${entry.unit}`
      : null);
  const rawLabel = entry.label || entry.id || "";
  const label = rawLabel.includes(" — ") ? rawLabel.split(" — ")[0] : rawLabel;
  return { label, sub: dims };
};

export default function CheckoutPage() {
  useCurrency(); // re-render prices on display-currency change
  useNoIndex();
  useEffect(() => { trackEvent("checkout_start"); }, []);
  const { items, subtotal, recurringSubtotal, recurringLabel, clear, setOpen } = useCart();
  // Display-only US$ list-price figures (AWG `subtotal` stays the
  // authoritative amount sent to the backend/Sentoo).
  const usdSubtotal = items.every((it) => Number(it.price_usd) > 0)
    ? items.reduce((sum, it) => sum + Number(it.price_usd) * (it.qty || 1), 0)
    : undefined;
  const usdRecurring = items.every((it) => !Number(it.recurring_price) || Number(it.recurring_price_usd) > 0)
    ? items.reduce((sum, it) => sum + Number(it.recurring_price_usd || 0), 0)
    : undefined;
  const [form, setForm] = useState({ name: "", email: "", phone: "", delivery_address: "", notes: "" });
  const [loading, setLoading] = useState(false);
  // Payment test-mode flag — sourced from /api/settings. When true the
  // page renders an unmistakable TEST MODE banner and re-labels the pay
  // button so no customer can click it thinking real money will move.
  const [testMode, setTestMode] = useState(false);
  // Site-wide tax broadcast — sourced from /api/settings via the
  // shared `useSalesTax` hook so no component hardcodes 0.07
  // (server still validates on submit). Review & Submit screens
  // in every configurator read the SAME hook — parity by design.
  const { taxRate, taxLabel, compute: computeTax } = useSalesTax();
  const { taxAmount, total: totalWithTax } = computeTax(subtotal);
  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Close the cart drawer the moment the customer lands on /checkout so
  // its backdrop cannot intercept clicks on the payment CTA.
  useEffect(() => { setOpen(false); }, [setOpen]);

  // Batch 1 (Feb 2026) — Services contact carry-forward via
  //   meta.project_brief.{contact_name, email, phone}
  // Batch 2 (Mar 2026) — Workstations contact carry-forward via
  //   meta.contact.{name, email, phone}
  // Both shapes are honoured by a single prefill effect: whichever
  // shape the most-recently-added line carries wins. All three
  // fields stay editable; no schema/persistence change. If a line
  // carries neither shape (e.g. an Existing-Logo path) the form
  // stays blank exactly as before. Runs once per cart-load.
  const [prefillDone, setPrefillDone] = useState(false);
  useEffect(() => {
    if (prefillDone || !items || items.length === 0) return;
    for (let i = items.length - 1; i >= 0; i--) {
      const meta = items[i]?.meta || {};
      const brief = meta.project_brief || {};
      const contact = meta.contact || {};
      const name = brief.contact_name || contact.name || "";
      const email = brief.email || contact.email || "";
      const phone = brief.phone || contact.phone || "";
      const delivery_address = contact.delivery_address || "";
      if (name || email || phone || delivery_address) {
        setForm((prev) => ({
          name: prev.name || name,
          email: prev.email || email,
          phone: prev.phone || phone,
          delivery_address: prev.delivery_address || delivery_address,
          notes: prev.notes,
        }));
        break;
      }
    }
    setPrefillDone(true);
  }, [items, prefillDone]);

  // Workstation delivery-address reassurance (Mar 2026). Extract
  // every unique `meta.contact.delivery_address` present in the cart
  // so a compact read-only "Deliver to:" panel can confirm the
  // customer's address was retained without asking them to retype
  // it. Purely presentational — the address itself continues to
  // persist through the existing `meta.contact` line-item field to
  // the order document.
  //
  // Feb 2026 — Delivery Address now collected on this checkout page
  // for workstation orders (was on the configurator). If the customer
  // typed an address into this form, that value drives the panel so
  // it updates live. Cart-line values remain a fallback for legacy
  // carts persisted before this change.
  const hasWorkstation = (items || []).some((it) => it?.kind === "laptop");
  // Any workstation carrying quote-required attached software marks
  // the whole order as awaiting final pricing. The customer still
  // completes checkout but sees an explicit notice so they never think
  // the on-screen total is final for those items.
  const hasAwaitingPricing = (items || []).some(
    (it) => !!(it?.meta && it.meta.awaiting_pricing_confirmation)
  );
  const cartDeliveryAddresses = Array.from(new Set(
    (items || [])
      .map((it) => (it?.meta?.contact?.delivery_address || "").trim())
      .filter(Boolean)
  ));
  const displayDeliveryAddresses = form.delivery_address.trim()
    ? [form.delivery_address.trim()]
    : cartDeliveryAddresses;

  useEffect(() => {
    api.get("/settings").then((r) => {
      setTestMode(!!r.data?.payment_test_mode);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setLoading(true);
    try {
      // Feb 2026 — for workstation orders the delivery address is
      // typed at checkout (moved off the configurator). Attach it to
      // the notes so it lands on the order document without needing
      // any backend schema change. Existing behavior for non-
      // workstation carts is unchanged.
      let combinedNotes = form.notes || "";
      if (hasWorkstation && form.delivery_address.trim()) {
        const addr = form.delivery_address.trim();
        combinedNotes = `Delivery Address:\n${addr}${combinedNotes ? `\n\n${combinedNotes}` : ""}`;
      }
      const res = await api.post("/orders", {
        // P0: submit canonical LineItemConfigs only. All commercial
        // values are computed server-side by the pricing authority.
        lines: items.map(toLineItemConfig),
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        notes: combinedNotes,
        display_currency: getCurrency(),
      });
      clear();
      window.location.href = res.data.redirect_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail && typeof detail === "object" ? detail.code : "";
      // Map server rejection codes to customer-safe messaging. The raw
      // reason (which may contain internal item_codes) is kept in the
      // network response for debugging but never shown to the customer.
      const friendly = {
        dependency_missing: "Some items in your cart require another item to be selected. Please review your cart and try again.",
        mutex_violation: "You've selected two options that can't be purchased together. Please remove one and try again.",
        quantity_above_max: "One of your items exceeds the maximum quantity allowed. Please reduce it and try again.",
        quantity_below_min: "One of your items has an invalid quantity. Please review your cart.",
        invalid_quantity: "One of your items has an invalid quantity. Please review your cart.",
        line_not_found: "One of your items is no longer available. Please remove it and try again.",
        unresolvable: "One of your items is no longer available for direct purchase. Please remove it or contact us for a quote.",
        inactive_item: "One of your items is no longer available. Please remove it and try again.",
        inactive_service: "One of your items is no longer available. Please remove it and try again.",
        estimate_only: "This item is available on request only — please use the estimate flow.",
        missing_identity: "One of your items is missing configuration. Please remove and re-add it.",
      }[code] || (typeof detail === "string" ? detail : "Could not start checkout. Please try again.");
      toast.error(friendly);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="bg-[#0F0F0F]">
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20">
        {testMode && (
          <div
            data-testid="checkout-test-mode-banner"
            className="mb-8 rounded-lg border border-amber-500/50 bg-amber-500/10 px-5 py-4 text-amber-200"
            role="alert"
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.24em]">Test Mode</div>
            <div className="mt-1 text-sm font-semibold">
              No payment will be processed. This checkout is a test transaction only —
              real money will not be charged and Ur Designs will not treat this as a paid order.
            </div>
          </div>
        )}
        <div className="ur-eyebrow">Checkout</div>
        <h1 className="font-display mt-4 text-4xl sm:text-5xl font-black tracking-tight">Secure Checkout</h1>
        <p className="mt-3 text-[#A3A3A3] text-sm flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#FF6600]" /> Payments processed by Sentoo (Aruba Bank).
        </p>

        <form onSubmit={submit} className="mt-10 grid lg:grid-cols-[1.4fr_1fr] gap-8">
          <div className="ur-card p-8 space-y-5">
            <h2 className="font-display text-xl font-bold">Your Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name" value={form.name} onChange={handle("name")} testid="checkout-name" required />
              <Field label="Email" type="email" value={form.email} onChange={handle("email")} testid="checkout-email" required />
              <Field label="Phone" value={form.phone} onChange={handle("phone")} testid="checkout-phone" />
            </div>
            {hasWorkstation && (
              <div>
                <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">
                  Delivery Address <span className="text-[#FF6600]">*</span>
                </label>
                <textarea
                  data-testid="checkout-delivery-address"
                  value={form.delivery_address}
                  onChange={handle("delivery_address")}
                  rows={3}
                  required
                  placeholder="Street, house number, area / district, Aruba"
                  className="mt-2 w-full bg-[#0F0F0F] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600] resize-none"
                />
                <p className="mt-1.5 text-[11px] text-[#A3A3A3] leading-snug">
                  Where should we deliver your workstation? Ur Designs sources, configures and delivers your workstation directly to you — there is no in-store pickup.
                </p>
              </div>
            )}
            {displayDeliveryAddresses.length > 0 && (
              // Workstation delivery-address reassurance (Mar 2026).
              // Read-only summary — the address remains persisted
              // via `meta.contact.delivery_address` on the line item
              // and flows into the order document unchanged. Renders
              // only for cart lines that actually carry an address
              // (Workstations); Service-only carts render nothing
              // here so this block never appears on a Services
              // checkout.
              <div
                data-testid="checkout-delivery-summary"
                className="rounded-xl border border-[#262626] bg-[#0F0F0F] px-4 py-3"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#A3A3A3]">
                  Deliver to
                </div>
                <div
                  data-testid="checkout-delivery-summary-address"
                  className="mt-1 text-sm text-white whitespace-pre-wrap leading-relaxed"
                >
                  {displayDeliveryAddresses.join("\n")}
                </div>
                <div className="mt-2 text-[11px] text-[#A3A3A3]">
                  Your workstation will be delivered here. Edit the address above to update.
                </div>
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Order Notes (optional)</label>
              <textarea
                data-testid="checkout-notes"
                value={form.notes}
                onChange={handle("notes")}
                rows={4}
                className="mt-2 w-full bg-[#0F0F0F] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600] resize-none"
              />
            </div>
          </div>

          <aside className="ur-card p-6 lg:sticky lg:top-28 h-fit">
            <h3 className="font-display text-lg font-bold">Order Summary</h3>
            <div className="mt-4 space-y-3 max-h-72 overflow-y-auto ur-scrollbar pr-2">
              {items.length === 0 && <p className="text-[#A3A3A3] text-sm">Your cart is empty.</p>}
              {items.map((it) => {
                const base = it.meta?.main_artwork_base_format;
                const baseDims = base?.dimensions
                  || (base?.width && base?.height && base?.unit
                    ? `${base.width} × ${base.height} ${base.unit}`
                    : null);
                const rawFormats = Array.isArray(it.meta?.selected_formats) ? it.meta.selected_formats : [];
                const paidFormats = rawFormats.map(normaliseFormatEntry).filter(Boolean);
                // For the "Main Artwork — Additional Size / Format" line
                // we present the paid additionals individually (one row
                // per format with its own +AWG 75) instead of a single
                // collapsed "× N" line — the dimensions are part of the
                // production specification the customer is paying for.
                const isAdditionalMainArtworkLine = paidFormats.length > 0
                  && typeof it.name === "string"
                  && it.name.includes("Main Artwork — Additional Size / Format");
                if (isAdditionalMainArtworkLine) {
                  return (
                    <div key={it.item_id} data-testid={`checkout-line-${it.item_id}`} className="space-y-2">
                      {paidFormats.map((f, idx) => (
                        <div
                          key={idx}
                          data-testid={`checkout-line-${it.item_id}-format-${idx}`}
                          className="flex items-start justify-between gap-4 text-sm"
                        >
                          <div className="min-w-0">
                            <div className="font-semibold truncate">Additional Main Artwork Format</div>
                            <div className="text-xs text-[#D1D1D1] truncate">{f.label}</div>
                            {f.sub && (
                              <div className="text-[11px] text-[#A3A3A3] mt-0.5">{f.sub}</div>
                            )}
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="font-display font-bold leading-none">{listMoney(MAIN_ARTWORK_FORMAT_PRICE, MAIN_ARTWORK_FORMAT_PRICE_USD)}</div>
                            <div className="text-[10px] text-[#A3A3A3] mt-0.5">{usdApprox(MAIN_ARTWORK_FORMAT_PRICE)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }
                return (
                  <div key={it.item_id} data-testid={`checkout-line-${it.item_id}`} className="space-y-1.5">
                    <div className="flex items-start justify-between gap-4 text-sm">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{it.name}</div>
                        <div className="text-xs text-[#A3A3A3]">{it.kind} · qty {it.qty || 1}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="font-display font-bold leading-none">{listMoney(it.price * (it.qty || 1), it.price_usd ? it.price_usd * (it.qty || 1) : undefined)}</div>
                        <div className="text-[10px] text-[#A3A3A3] mt-0.5">{usdApprox(it.price * (it.qty || 1))}</div>
                      </div>
                    </div>
                    {/* Workstation-attached software (Feb 2026 unified
                        commerce). Rendered as sub-rows so the customer
                        sees the full workstation configuration on
                        checkout without a separate cart line. PRICED
                        items would contribute to subtotal via the
                        pricing authority (future); QUOTE_REQUIRED
                        rows explicitly render "Price to be confirmed"
                        and NEVER count toward payable total. */}
                    {Array.isArray(it.meta?.attached_software) && it.meta.attached_software.length > 0 && (
                      <div
                        data-testid={`checkout-line-${it.item_id}-attached-software`}
                        className="rounded-md border border-[#1F1F1F] bg-[#0A0A0A] px-3 py-2.5 space-y-2"
                      >
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#FF6600]">
                          Software Selections
                        </div>
                        {it.meta.attached_software.map((sw, idx) => {
                          const isPriced = sw.state === "priced";
                          const sel = Array.isArray(sw.selections) ? sw.selections : [];
                          const selText = sel
                            .filter((s) => s.option_label)
                            .map((s) => s.option_label)
                            .join(" · ");
                          return (
                            <div
                              key={idx}
                              data-testid={`checkout-line-${it.item_id}-attached-${sw.power_slug || idx}`}
                              className="flex items-start justify-between gap-3 text-xs"
                            >
                              <div className="min-w-0">
                                <div className="text-[#D1D1D1] font-semibold truncate">{sw.name}</div>
                                {selText && (
                                  <div className="text-[10px] text-[#A3A3A3] mt-0.5">{selText}</div>
                                )}
                              </div>
                              <div className="shrink-0 text-right">
                                {isPriced ? (
                                  <span className="text-[#D1D1D1] font-semibold tabular-nums">
                                    {money(Number(sw.unit_price || 0) * Number(sw.quantity || 1))}
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#F59E0B]">
                                    Price to be confirmed
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {Array.isArray(it.meta?.external_setup_selections) && it.meta.external_setup_selections.length > 0 && (
                      <div
                        data-testid={`checkout-line-${it.item_id}-external-setup`}
                        className="rounded-md border border-[#1F1F1F] bg-[#0A0A0A] px-3 py-2 space-y-1"
                      >
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#A3A3A3]">
                          Setup Support Requested
                        </div>
                        {it.meta.external_setup_selections.map((ext, idx) => (
                          <div key={idx} className="text-[11px] text-[#B5B5B5]">
                            {ext.name}
                            <span className="text-[10px] text-[#7A7A7A] ml-1.5">(customer subscription)</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {base?.label && (
                      <div
                        data-testid={`checkout-line-${it.item_id}-base-format`}
                        className="rounded-md border border-[#1F1F1F] bg-[#0A0A0A] px-3 py-2 text-xs flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#FF6600]">Main Artwork Format</div>
                          <div className="text-[#D1D1D1] mt-0.5 truncate">
                            {base.label.includes(" — ") ? base.label.split(" — ")[0] : base.label}
                          </div>
                          {baseDims && (
                            <div className="text-[10px] text-[#A3A3A3] mt-0.5">{baseDims}</div>
                          )}
                        </div>
                        <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.14em] text-[#4CB782] mt-0.5">
                          Included
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-5 pt-5 border-t border-[#262626] space-y-2 text-sm">
              <div className="flex justify-between items-baseline">
                <span className="text-[#A3A3A3]">Subtotal</span>
                <span data-testid="checkout-subtotal">{money(subtotal, usdSubtotal)} <span className="text-[11px] text-[#A3A3A3] ml-1">{usdApprox(subtotal)}</span></span>
              </div>
              {taxRate > 0 && (
                <div className="flex justify-between items-baseline">
                  <span className="text-[#A3A3A3]" data-testid="checkout-tax-label">{taxLabel}</span>
                  <span data-testid="checkout-tax-amount">{money(taxAmount, usdSubtotal != null ? usdSubtotal * taxRate : undefined)}</span>
                </div>
              )}
              <div className="flex justify-between items-end font-display text-2xl font-black">
                <span>Due Now</span>
                <div className="text-right">
                  <span data-testid="checkout-total" className="block leading-none">{money(totalWithTax, usdSubtotal != null ? usdSubtotal * (1 + taxRate) : undefined)}</span>
                  <span className="text-[11px] font-manrope font-normal text-[#A3A3A3] mt-1 inline-block">{usdApprox(totalWithTax)}</span>
                </div>
              </div>
              <ChargeDisclosure awgTotal={totalWithTax} testId="checkout-charge-disclosure" />
              {hasAwaitingPricing && (
                <div
                  data-testid="checkout-awaiting-pricing-notice"
                  className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2.5 text-[11px] text-amber-200 leading-relaxed"
                >
                  <span className="font-bold uppercase tracking-wider">Final pricing pending</span>{" "}
                  — some software items on this order require final pricing. Ur Designs will confirm those prices before any additional charge; the total above covers only the priced items and taxes.
                </div>
              )}
              {recurringSubtotal > 0 && (
                <div
                  data-testid="checkout-recurring-block"
                  className="mt-4 rounded-lg border border-[#FF6600]/40 bg-[#FF6600]/5 px-4 py-3"
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF6600]">
                    Ongoing Website Care
                  </div>
                  <div className="mt-1 flex items-baseline justify-between">
                    <span className="text-sm text-[#D1D1D1]">
                      Starts after your site launches
                    </span>
                    <span
                      data-testid="checkout-recurring-amount"
                      className="font-display text-lg font-bold text-white whitespace-nowrap"
                    >
                      {listMoney(recurringSubtotal, usdRecurring)}/{recurringLabel || "month"}
                    </span>
                  </div>
                  <p className="mt-2 text-[11px] text-[#A3A3A3] leading-relaxed">
                    Today's checkout collects the one-time project total only.
                    Website Care is billed separately once your site is live —
                    we'll confirm the schedule with you before any monthly charge.
                  </p>
                </div>
              )}
            </div>
            <button
              data-testid="checkout-submit"
              type="submit"
              disabled={loading || items.length === 0}
              className="ur-btn-primary w-full mt-6 disabled:opacity-40"
            >
              {loading
                ? "Redirecting…"
                : (testMode
                    ? (<>Simulate Payment (Test Mode) <Lock className="w-4 h-4" /></>)
                    : (<>Pay with Sentoo <Lock className="w-4 h-4" /></>))}
            </button>
            <p className="mt-3 text-[11px] text-[#A3A3A3] text-center">You'll be redirected to a secure Sentoo payment page.</p>
          </aside>
        </form>
      </section>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid, required }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="mt-2 w-full bg-[#0F0F0F] border border-[#262626] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF6600]"
    />
  </div>
);
